import React from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { Grid, Row, Col, Clearfix } from 'react-bootstrap';
import { ComposedChart, PieChart, Line, Pie, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Cell } from 'recharts';
import moment from 'moment';
import 'moment-timezone';
import { find as _find } from 'lodash';

import { changeActivePage } from 'actions/ListActions';
import { parseQs } from 'helpers/http';
import { generateMetaTags } from 'helpers/metaTags';
import { getColor } from 'helpers/charts';

import Breadcrumb from 'components/layout/Breadcrumb';
import Loader from 'components/layout/Loader';

import DashboardWidget12 from 'components/layout/dashboard/DashboardWidget12';
import DashboardWidget16 from 'components/layout/dashboard/DashboardWidget16';

import LeadStatsByReactionTime from 'components/layout/lead/stats/LeadStatsByReactionTime';
import AssigneeStatsDetails from 'components/layout/lead/stats/AssigneeStatsDetails';
import CrStatsDetails from 'components/layout/lead/stats/CrStatsDetails';
import SourcesStatsDetails from 'components/layout/lead/stats/SourcesStatsDetails';

import Data from 'components/Data';

import ListFilterPipeline from 'components/layout/list/filters/ListFilterPipeline';
import ListFilterHoldingCompany from 'components/layout/list/filters/ListFilterHoldingCompany';
import ListFilterAssignee from 'components/layout/list/filters/ListFilterAssignee';
import ListFilterDateBetween from 'components/layout/list/filters/ListFilterDateBetween';
import ListFilterLeadSource from 'components/layout/list/filters/ListFilterLeadSource';
import ListFilterLeadTag from 'components/layout/list/filters/ListFilterLeadTag';
import ListFilterReset from 'components/layout/list/ListFilterReset';

import DocMeta from 'components/DocMeta';

const customLabelUsernameCount = ({ cx, cy, fill, midAngle, innerRadius, outerRadius, name, count, percent }) => {
  const RADIAN = Math.PI / 180;
  const radius = 5 + innerRadius + (outerRadius - innerRadius);
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return percent > 0.03 ? (
    <text x={x} y={y} fill={fill} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize="10">
      {name} ({count})
    </text>
  ) : null;
};

const customLabelUsernameCr = ({ cx, cy, fill, midAngle, innerRadius, outerRadius, name, cr, percent }) => {
  const RADIAN = Math.PI / 180;
  const radius = 5 + innerRadius + (outerRadius - innerRadius);
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return percent > 0.03 ? (
    <text x={x} y={y} fill={fill} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize="10">
      {name} ({cr}%)
    </text>
  ) : null;
};

const customLabelSource = ({ cx, cy, fill, midAngle, innerRadius, outerRadius, id, count, percent }) => {
  const RADIAN = Math.PI / 180;
  const radius = 5 + innerRadius + (outerRadius - innerRadius);
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return percent > 0.03 ? (
    <text x={x} y={y} fill={fill} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize="10">
      {id} ({count})
    </text>
  ) : null;
};

class LeadStatistics extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedYear: moment().year(),
      selectedMonth: 'all', // all for the yearly view, 0-11 for the month view
    };
  }

  onBarClick = value => {
    const {
      payload: {
        id: { month },
      },
    } = value;

    this.setState(prevState => ({ selectedMonth: prevState.selectedMonth === 'all' ? month - 1 : 'all' }));
  };

  getContent = () => {
    const {
      intl: { messages },
      leadStatsAssignee,
      leadStatsSource,
      leadStatsTime,
      leadStatsMonthTime,
    } = this.props;
    const { selectedYear, selectedMonth } = this.state;

    // assignees: conversion rate:
    const assigneesChartData = [];
    let allLeadsCount = 0;
    let allLeadsWonCount = 0;
    if (!leadStatsAssignee.pending) {
      for (let i = 0; i < leadStatsAssignee.data.by_assignee.length; i++) {
        const assignee = leadStatsAssignee.data.by_assignee[i];
        assigneesChartData.push({
          name: assignee.name,
          count: 0,
          cr: parseInt((assignee.won / assignee.count) * 100, 10),
          ...assignee,
        });

        allLeadsCount += assignee.count;
        allLeadsWonCount += assignee.won;
      }
    }
    const globalCr = allLeadsCount ? parseInt((allLeadsWonCount / allLeadsCount) * 100, 10) : 0;

    // lead list - year view
    const yearChartData = [];
    const availableYears = [];
    if (!leadStatsTime.pending) {
      if (selectedMonth === 'all') {
        for (let i = 0; i < 12; i++) {
          const monthData = _find(leadStatsTime.data.by_time, ['id', { month: i + 1, year: selectedYear }]) || {};

          yearChartData.push({
            ...monthData,
            name: i + 1 + '.' + selectedYear,
            count: 0,
            open: monthData.count - monthData.won - monthData.lost || 0,
            won: monthData.won || 0,
            lost: monthData.lost || 0,
            cr: monthData.count ? parseInt((monthData.won / monthData.count) * 100, 10) : 0,
          });
        }
      }

      for (let i = 0; i < leadStatsTime.data.by_time.length; i++) {
        if (availableYears.indexOf(leadStatsTime.data.by_time[i].id.year) < 0) {
          availableYears.push(leadStatsTime.data.by_time[i].id.year);
        }
      }
    }

    // lead list - month view
    const monthChartData = [];
    if (!leadStatsMonthTime.pending && selectedMonth !== 'all') {
      let dateIteration = moment.tz({ years: selectedYear, months: selectedMonth, days: 1 }, 'UTC');

      for (let i = 0; dateIteration.month() === selectedMonth; i++) {
        const dayData = _find(leadStatsMonthTime.data.by_time, ['id', { day: i + 1 }]) || {};

        monthChartData.push({
          ...dayData,
          name: dateIteration.format('D'),
          count: 0,
          open: dayData.count - dayData.won - dayData.lost || 0,
          won: dayData.won || 0,
          lost: dayData.lost || 0,
          cr: dayData.count ? parseInt((dayData.won / dayData.count) * 100, 10) : 0,
        });

        dateIteration = dateIteration.add(1, 'day');
      }
    }

    const currentLocaleData = moment.localeData();

    return (
      <>
        {leadStatsAssignee.pending ||
        leadStatsSource.pending ||
        leadStatsTime.pending ||
        (selectedMonth !== 'all' && leadStatsMonthTime.pending) ? (
          <Loader />
        ) : (
          <Grid fluid className="no-padding">
            <Row>
              <Col xs={12}>
                <DashboardWidget12
                  title={messages.leads}
                  options={availableYears.sort().map(year => {
                    const isActive = year === selectedYear;
                    return (
                      <li key={year} className={isActive ? 'active ' : ''}>
                        <a
                          href="#changeYear"
                          class={'font-secondary text-master ' + (isActive ? 'bg-master-light ' : '')}
                          onClick={e => {
                            e.preventDefault();
                            this.setState({ selectedYear: year });
                          }}
                        >
                          {year}
                        </a>
                      </li>
                    );
                  })}
                  moreOptions={[
                    currentLocaleData._monthsShort.map((monthName, monthIndex) => {
                      const isActive = monthIndex === selectedMonth;

                      return (
                        <React.Fragment key={monthName}>
                          {!monthIndex && (
                            <li className={selectedMonth === 'all' ? 'active ' : ''}>
                              <a
                                href="#changeStatOption"
                                class={
                                  'font-secondary text-master ' + (selectedMonth === 'all' ? 'bg-master-light ' : '')
                                }
                                onClick={e => {
                                  e.preventDefault();
                                  this.setState({ selectedMonth: 'all' });
                                }}
                              >
                                {messages.all}
                              </a>
                            </li>
                          )}

                          <li className={isActive ? 'active ' : ''}>
                            <a
                              href="#changeStatOption"
                              class={'font-secondary text-master ' + (isActive ? 'bg-master-light ' : '')}
                              onClick={e => {
                                e.preventDefault();
                                this.setState({ selectedMonth: monthIndex });
                              }}
                            >
                              {monthName}
                            </a>
                          </li>
                        </React.Fragment>
                      );
                    }),
                  ]}
                  chart={
                    <ComposedChart
                      width={730}
                      height={250}
                      data={selectedMonth === 'all' ? yearChartData : monthChartData}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" hide domain={[0, 100]} />
                      <Tooltip
                        content={value => {
                          return value.payload && value.payload.length ? (
                            <div className="bg-white b-rad-lg padding-10 shadow-1">
                              <div className="bold m-b-5">{value.label}</div>
                              <div>
                                {messages.open}:{' '}
                                <span className="text-master">{_find(value.payload, { dataKey: 'open' }).value}</span>
                              </div>
                              <div>
                                {messages.won}:{' '}
                                <span className="text-success">{_find(value.payload, { dataKey: 'won' }).value}</span>
                              </div>
                              <div>
                                {messages.lost}:{' '}
                                <span className="text-danger">{_find(value.payload, { dataKey: 'lost' }).value}</span>
                              </div>
                              <div>CR: {_find(value.payload, { dataKey: 'cr' }).value}%</div>
                            </div>
                          ) : null;
                        }}
                      />
                      <Legend />
                      <Bar
                        yAxisId="left"
                        dataKey="lost"
                        stackId="a"
                        fill="red"
                        className="bg-danger"
                        onClick={this.onBarClick}
                        cursor="pointer"
                      />
                      <Bar
                        yAxisId="left"
                        dataKey="won"
                        stackId="a"
                        fill="green"
                        className="bg-success"
                        onClick={this.onBarClick}
                        cursor="pointer"
                      />
                      <Bar
                        yAxisId="left"
                        dataKey="open"
                        stackId="a"
                        fill="gold"
                        className="bg-master-light"
                        onClick={this.onBarClick}
                        cursor="pointer"
                      />
                      <Line yAxisId="right" type="monotone" dataKey="cr" stroke="#82ca9d" />
                    </ComposedChart>
                  }
                />
              </Col>
            </Row>

            <Row className="p-t-20">
              <Col xs={12} sm={4}>
                <DashboardWidget16
                  title="Lead Zuweisung"
                  value={allLeadsCount}
                  bgClass=""
                  chart={
                    <PieChart>
                      <Pie
                        dataKey="count"
                        data={assigneesChartData}
                        fill="white"
                        stroke="rgba(255, 255, 255, .6)"
                        label={customLabelUsernameCount}
                        labelLine={false}
                        isAnimationActive={false}
                      >
                        {assigneesChartData.map((assigneeChartCell, i) => (
                          <Cell key={assigneeChartCell.id} fill={'#' + getColor(i, assigneesChartData.length - 1)} />
                        ))}
                      </Pie>
                    </PieChart>
                  }
                  details={<AssigneeStatsDetails data={assigneesChartData} />}
                />
              </Col>

              <Col xs={12} sm={4}>
                <DashboardWidget16
                  title="Conversion rate"
                  value={globalCr + '%'}
                  bgClass=""
                  chart={
                    <PieChart>
                      <Pie
                        dataKey="cr"
                        data={assigneesChartData}
                        fill="white"
                        stroke="rgba(255,255,255,.6)"
                        label={customLabelUsernameCr}
                        labelLine={false}
                        isAnimationActive={false}
                      >
                        {assigneesChartData.map((assigneeChartCell, i) => (
                          <Cell key={assigneeChartCell.id} fill={'#' + getColor(i, assigneesChartData.length - 1)} />
                        ))}
                      </Pie>
                    </PieChart>
                  }
                  details={<CrStatsDetails data={assigneesChartData} />}
                />
              </Col>

              <Col xs={12} sm={4}>
                <DashboardWidget16
                  title="Quellen"
                  value=""
                  bgClass=""
                  chart={
                    <PieChart>
                      <Pie
                        dataKey="count"
                        data={leadStatsSource.data.by_source}
                        fill="white"
                        stroke="rgba(255,255,255,.6)"
                        label={customLabelSource}
                        labelLine={false}
                        isAnimationActive={false}
                      >
                        {leadStatsSource.data.by_source.map((leadStatsCell, i) => (
                          <Cell
                            key={leadStatsCell.id}
                            fill={'#' + getColor(i, leadStatsSource.data.by_source.length - 1)}
                          />
                        ))}
                      </Pie>
                    </PieChart>
                  }
                  details={<SourcesStatsDetails data={leadStatsSource.data.by_source} />}
                />
              </Col>
            </Row>
          </Grid>
        )}
      </>
    );
  };

  render() {
    const {
      intl: { messages },
      appIntl,
      type,
      leadStatsAssignee,
      leadStatsSource,
      leadStatsTime,
      location,
      user,
    } = this.props;
    const { selectedYear, selectedMonth } = this.state;

    const query = parseQs(location.search);

    const urlData = { filters: {} };
    if (query.pipeline) {
      urlData.filters.pipeline = query.pipeline;
    }
    if (query.assignee) {
      urlData.filters.assignee = query.assignee;
    }
    if (query.owner) {
      urlData.filters.owner = query.owner;
    }
    if (query.source) {
      urlData.filters.source = query.source;
    }
    if (query.tags) {
      urlData.filters.tags = query.tags;
    }
    if (query.dateFrom) {
      urlData.dateFrom = moment.tz(query.dateFrom, 'UTC').format('YYYY-MM-DD 00:00:00');
    }
    if (query.dateTo) {
      urlData.dateTo = moment.tz(query.dateTo, 'UTC').format('YYYY-MM-DD 23:59:59');
    }

    const timezone = user && user.profile ? user.profile.timezone : 'UTC';

    const content = this.getContent();
    const filters = [];

    filters.push(<ListFilterPipeline key="pipeline" />);
    filters.push(<ListFilterHoldingCompany key="holding-company" />);
    filters.push(<ListFilterAssignee key="assignee" />);

    let filterBetweenMinDate = moment();
    if (!leadStatsTime.pending) {
      for (let i = 0; i < leadStatsTime.data.by_time.length; i++) {
        const leadDate = moment.tz(
          leadStatsTime.data.by_time[i].id.year + '-' + leadStatsTime.data.by_time[i].id.month + '-1',
          timezone
        );

        if (filterBetweenMinDate.isAfter(leadDate)) {
          filterBetweenMinDate = leadDate;
        }
      }

      filters.push(
        <ListFilterDateBetween
          label={messages.date}
          dateFrom={query.dateFrom}
          dateTo={query.dateTo}
          fromMinDate={filterBetweenMinDate}
          fromMaxDate={moment()}
          toMinDate={filterBetweenMinDate}
          toMaxDate={moment()}
          key="date-between"
        />
      );
    }

    filters.push(<ListFilterLeadSource key="source" />);
    filters.push(<ListFilterLeadTag key="tag" />);
    filters.push(
      <ListFilterReset
        key="reset-all"
        availableFilters={['pipeline', 'owner', 'assignee', 'dateFrom', 'dateTo', 'source', 'tag']}
      />
    );

    return (
      <DocMeta tags={generateMetaTags(messages.meta.leads)}>
        <div>
          <Data
            dataName="leadStatsAssignee"
            url="/api/leads/stats/assignee.json"
            data={leadStatsAssignee}
            requestData={urlData}
          />
          <Data dataName="leadStatsSource" url="/api/leads/stats/source.json" requestData={urlData} />
          <Data dataName="leadStatsTime" url="/api/leads/stats/time.json" requestData={urlData} />
          {selectedMonth !== 'all' && (
            <Data
              dataName="leadStatsMonthTime"
              url={`/api/leads/stats/years/${selectedYear}/months/${selectedMonth + 1}/time.json`}
              requestData={urlData}
            />
          )}

          <div className="padding-25 sm-padding-10 bg-white">
            <Breadcrumb
              crumbs={[
                { link: appIntl.urlPrefix + 'account', name: 'Dashboard' },
                { link: appIntl.urlPrefix + 'account/' + type + 's', name: messages[type + 's'] },
                { link: null, name: messages.statistics },
              ]}
              className="p-t-0"
            />

            <h4 className="bold">
              <i className="far fa-chart-pie" /> {messages.statistics}
            </h4>

            <Clearfix>{filters}</Clearfix>
          </div>

          <div className="padding-25 sm-padding-10">
            {content}

            <div className="m-t-15">
              {!leadStatsAssignee.pending && !leadStatsSource.pending && (
                <LeadStatsByReactionTime
                  data={{
                    ...leadStatsAssignee.data,
                    by_source: leadStatsSource.data.by_source_and_reaction_time,
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </DocMeta>
    );
  }
}
LeadStatistics.defaultProps = {
  type: 'lead',
};

const mapStateToProps = state => {
  return {
    appIntl: state.intl,
    pipelines: state.list.pipelines,
    leadStatsAssignee: state.data.leadStatsAssignee,
    leadStatsSource: state.data.leadStatsSource,
    leadStatsTime: state.data.leadStatsTime,
    leadStatsMonthTime: state.data.leadStatsMonthTime,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    changeActivePage: (listName, activePage, resPerPage) => {
      dispatch(changeActivePage(listName, activePage, resPerPage));
    },
  };
};

export default injectIntl(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(LeadStatistics)
);
